import type { InterviewStreamPlugin } from '../interview-stream-host.type'

type TrackListener = () => void

export function createCameraTrackMonitorPlugin(): InterviewStreamPlugin {
    let ctxRef: Parameters<NonNullable<InterviewStreamPlugin['onStreamsUpdated']>>[0] | null = null
    let listeners: TrackListener[] = []
    let wasVideoActive = false

    const cleanupListeners = () => {
        listeners.forEach((remove) => remove())
        listeners = []
    }

    const isTrackActive = (track: MediaStreamTrack | null) => {
        return Boolean(track && track.readyState === 'live' && track.enabled && !track.muted)
    }

    const attachTrackListeners = (track: MediaStreamTrack | null) => {
        cleanupListeners()
        if (!track) {
            // replaceCameraTrack(null) notifies with a null track after listeners on the
            // previous track are torn down — treat that as camera-off if we were active.
            if (wasVideoActive && ctxRef) {
                wasVideoActive = false
                ctxRef.showCameraOffWarning()
            }
            return
        }

        const onChange = () => {
            if (!ctxRef) {
                return
            }
            const active = isTrackActive(track)
            if (!active) {
                wasVideoActive = false
                ctxRef.showCameraOffWarning()
                return
            }
            if (!wasVideoActive) {
                wasVideoActive = true
                ctxRef.clearCameraOffWarning()
            }
        }

        const events = ['ended', 'mute', 'unmute'] as const
        for (const event of events) {
            track.addEventListener(event, onChange)
            listeners.push(() => track.removeEventListener(event, onChange))
        }

        // Always evaluate: inactive → warn; inactive→active (e.g. reattach) → clear.
        onChange()
    }

    return {
        id: 'camera-track-monitor',
        start(ctx) {
            ctxRef = ctx
            attachTrackListeners(ctx.streams.videoTrack)
        },
        stop() {
            cleanupListeners()
            ctxRef = null
            wasVideoActive = false
        },
        onStreamsUpdated(ctx) {
            ctxRef = ctx
            attachTrackListeners(ctx.streams.videoTrack)
        },
    }
}
